import { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import AdminSidebar from "../../components/admin/AdminSidebar"
import AdminHeader from "../../components/admin/AdminHeader"
import { useCourseEnrollments } from "../../hooks/useCourseEnrollments"
import { useGet } from "../../utils/apiComponent/useApi"
import { endpoints } from "../../utils/endpoints"
import { toastUtils } from "../../utils/toast"
import {
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Calendar,
  User,
  Users,
  BookOpen,
  Ban,
  Unlock,
  Plus,
  X,
  Clock,
  Infinity
} from "lucide-react"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"

export default function CourseEnrollmentsPage() {
  const navigate = useNavigate()
  const [selectedCourseId, setSelectedCourseId] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterStatus, setFilterStatus] = useState("all")
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [enrollments, setEnrollments] = useState([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [selectedEnrollment, setSelectedEnrollment] = useState(null)
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false)
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false)

  const { loading, getCourseUsers, updateEnrollmentAccess, deleteEnrollment, activateCourseForUsers } = useCourseEnrollments()
  
  // États pour le formulaire d'activation
  const [userSearchTerm, setUserSearchTerm] = useState("")
  const [selectedUserIds, setSelectedUserIds] = useState([])
  const [accessDurationDays, setAccessDurationDays] = useState("")
  const [isSubmittingActivation, setIsSubmittingActivation] = useState(false)
  
  // Récupérer la liste des cours
  const { data: coursesData } = useGet(endpoints.courses)
  
  // Récupérer la liste des utilisateurs pour le formulaire d'activation
  const usersUrl = useMemo(() => {
    const params = new URLSearchParams()
    params.append('limit', '100')
    if (userSearchTerm) {
      params.append('search', userSearchTerm)
    }
    return `${endpoints.users}?${params.toString()}`
  }, [userSearchTerm])
  
  const { data: usersData, execute: refetchUsers, loading: loadingUsers } = useGet(
    usersUrl,
    { immediate: false }
  )
  
  const usersList = Array.isArray(usersData) 
    ? usersData 
    : (usersData?.data || usersData?.users || [])
  
  const coursesList = Array.isArray(coursesData) 
    ? coursesData 
    : (coursesData?.courses || coursesData?.data?.courses || [])

  useEffect(() => {
    if (selectedCourseId) {
      loadEnrollments()
    }
  }, [selectedCourseId, page, searchTerm, filterStatus])

  // Charger les utilisateurs quand le modal d'activation est ouvert
  useEffect(() => {
    if (isActivateModalOpen) {
      refetchUsers()
    }
  }, [isActivateModalOpen, userSearchTerm])

  const loadEnrollments = async () => {
    if (!selectedCourseId) return

    try {
      const filters = {
        page,
        limit,
        search: searchTerm || undefined,
        status: filterStatus === "all" ? undefined : filterStatus
      }
      
      const response = await getCourseUsers(selectedCourseId, filters)
      
      if (response) {
        if (Array.isArray(response)) {
          setEnrollments(response)
          setTotal(response.length)
        } else {
          setEnrollments(response.data || [])
          setTotal(response.total || 0)
          setTotalPages(response.totalPages || 1)
        }
      }
    } catch (error) {
      toastUtils.error(error?.message || "Erreur lors du chargement des utilisateurs")
    }
  }

  const handleDelete = async (enrollment) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir retirer ${enrollment.user?.name || 'cet utilisateur'} du cours ?`)) {
      return
    }

    try {
      await deleteEnrollment(enrollment.id)
      toastUtils.success("Utilisateur retiré du cours avec succès")
      loadEnrollments()
    } catch (error) {
      toastUtils.error(error?.message || "Erreur lors de la suppression")
    }
  }

  const handleUpdateAccess = async (accessData) => {
    try {
      await updateEnrollmentAccess(selectedEnrollment.id, accessData)
      toastUtils.success("Accès mis à jour avec succès")
      setIsAccessModalOpen(false)
      setSelectedEnrollment(null)
      loadEnrollments()
    } catch (error) {
      toastUtils.error(error?.message || "Erreur lors de la mise à jour")
    }
  }

  const handleUnlimitedAccess = async (enrollment) => {
    try {
      await updateEnrollmentAccess(enrollment.id, { accessExpiresAt: null })
      toastUtils.success("Accès illimité activé avec succès")
      loadEnrollments()
    } catch (error) {
      toastUtils.error(error?.message || "Erreur lors de l'activation de l'accès illimité")
    }
  }

  const handleUnblock = async (enrollment) => {
    try {
      await updateEnrollmentAccess(enrollment.id, { unblockAccess: true })
      toastUtils.success("Accès débloqué avec succès")
      loadEnrollments()
    } catch (error) {
      toastUtils.error(error?.message || "Erreur lors du déblocage")
    }
  }

  const handleBlock = async (enrollment) => {
    try {
      await updateEnrollmentAccess(enrollment.id, {
        accessBlockedAt: new Date().toISOString(),
        accessBlockedReason: "MANUAL_BLOCK"
      })
      toastUtils.success("Accès bloqué avec succès")
      loadEnrollments()
    } catch (error) {
      toastUtils.error(error?.message || "Erreur lors du blocage")
    }
  }

  const handleEditAccess = (enrollment) => {
    setSelectedEnrollment(enrollment)
    setIsAccessModalOpen(true)
  }

  const handleActivateCourse = async (e) => {
    e.preventDefault()
    
    if (!selectedCourseId) {
      toastUtils.error("Veuillez sélectionner un cours")
      return
    }
    
    if (selectedUserIds.length === 0) {
      toastUtils.error("Veuillez sélectionner au moins un utilisateur")
      return
    }
    
    setIsSubmittingActivation(true)
    
    try {
      const duration = accessDurationDays ? parseInt(accessDurationDays, 10) : null
      await activateCourseForUsers(selectedCourseId, selectedUserIds, duration)
      toastUtils.success(`Cours activé avec succès pour ${selectedUserIds.length} utilisateur(s)`)
      
      // Réinitialiser le formulaire
      setSelectedUserIds([])
      setUserSearchTerm("")
      setAccessDurationDays("")
      setIsActivateModalOpen(false)
      
      // Recharger la liste des utilisateurs du cours
      if (selectedCourseId) {
        loadEnrollments()
      }
    } catch (error) {
      const errorMessage = Array.isArray(error?.response?.data?.message)
        ? error.response.data.message[0]
        : error?.response?.data?.message || error?.message || "Erreur lors de l'activation"
      toastUtils.error(errorMessage)
    } finally {
      setIsSubmittingActivation(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (enrollment) => {
    const status = enrollment.status || "ENROLLED"
    const isBlocked = enrollment.accessBlockedAt !== null
    const isExpired = enrollment.accessExpiresAt && new Date(enrollment.accessExpiresAt) < new Date()

    if (isBlocked) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <Ban size={12} className="mr-1" />
          Bloqué
        </span>
      )
    }

    if (isExpired) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <XCircle size={12} className="mr-1" />
          Expiré
        </span>
      )
    }

    const statusLabels = {
      ENROLLED: "Inscrit",
      IN_PROGRESS: "En cours",
      COMPLETED: "Terminé",
      DROPPED: "Abandonné"
    }

    const statusColors = {
      ENROLLED: "bg-blue-100 text-blue-800",
      IN_PROGRESS: "bg-yellow-100 text-yellow-800",
      COMPLETED: "bg-green-100 text-green-800",
      DROPPED: "bg-gray-100 text-gray-800"
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || statusColors.ENROLLED}`}>
        <CheckCircle size={12} className="mr-1" />
        {statusLabels[status] || status}
      </span>
    )
  }

  const selectedCourse = coursesList.find(c => c.id === selectedCourseId)

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <AdminHeader 
          title="Gestion des Utilisateurs de Cours" 
          subtitle="Gérer les inscriptions et accès aux cours"
        />

        {/* Course Selection */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sélectionner un cours
          </label>
          <select
            value={selectedCourseId || ""}
            onChange={(e) => {
              setSelectedCourseId(e.target.value || null)
              setPage(1)
            }}
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent"
          >
            <option value="">-- Sélectionner un cours --</option>
            {coursesList.map((course) => (
              <option key={course.id} value={course.id}>
                {course.title}
              </option>
            ))}
          </select>
        </div>

        {!selectedCourseId ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <BookOpen className="mx-auto mb-4 text-gray-400" size={48} />
            <p className="text-gray-600">Sélectionnez un cours pour voir ses utilisateurs</p>
          </div>
        ) : (
          <>
            {/* Actions Bar */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Cours:</span> {selectedCourse?.title}
                </div>
                
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Rechercher un utilisateur..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value)
                      setPage(1)
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                  />
                </div>

                {/* Filter */}
                <select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value)
                    setPage(1)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="ENROLLED">Inscrit</option>
                  <option value="IN_PROGRESS">En cours</option>
                  <option value="COMPLETED">Terminé</option>
                  <option value="DROPPED">Abandonné</option>
                </select>
              </div>

              <Button 
                onClick={() => setIsActivateModalOpen(true)}
                className="bg-[#141835] hover:bg-[#203a8a]"
              >
                <Plus size={20} className="mr-2" />
                Activer pour utilisateurs
              </Button>
            </div>

            {/* Enrollments Table */}
            {loading ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#141835] mx-auto mb-4"></div>
                <p className="text-gray-600">Chargement des utilisateurs...</p>
              </div>
            ) : enrollments.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <Users className="mx-auto mb-4 text-gray-400" size={48} />
                <p className="text-gray-600">Aucun utilisateur trouvé</p>
              </div>
            ) : (
              <>
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inscrit le</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expire le</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {enrollments.map((enrollment) => (
                        <tr key={enrollment.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#141835] flex items-center justify-center text-white font-medium">
                                {enrollment.user?.name?.charAt(0)?.toUpperCase() || "U"}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {enrollment.user?.name || "N/A"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {enrollment.user?.email || "N/A"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(enrollment)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(enrollment.enrolledAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(enrollment.accessExpiresAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleEditAccess(enrollment)}
                                className="text-blue-600 hover:text-blue-800"
                                title="Modifier l'accès"
                              >
                                <Edit size={18} />
                              </button>
                              {enrollment.accessExpiresAt !== null ? (
                                <button
                                  onClick={() => handleUnlimitedAccess(enrollment)}
                                  className="text-purple-600 hover:text-purple-800"
                                  title="Rendre l'accès illimité"
                                >
                                  <Infinity size={18} />
                                </button>
                              ) : null}
                              {enrollment.accessBlockedAt ? (
                                <button
                                  onClick={() => handleUnblock(enrollment)}
                                  className="text-green-600 hover:text-green-800"
                                  title="Débloquer l'accès"
                                >
                                  <Unlock size={18} />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleBlock(enrollment)}
                                  className="text-orange-600 hover:text-orange-800"
                                  title="Bloquer l'accès"
                                >
                                  <Ban size={18} />
                                </button>
                              )}
                              <button
                                onClick={() => handleDelete(enrollment)}
                                className="text-red-600 hover:text-red-800"
                                title="Retirer du cours"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Page {page} sur {totalPages} ({total} utilisateurs)
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        variant="outline"
                      >
                        Précédent
                      </Button>
                      <Button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        variant="outline"
                      >
                        Suivant
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Access Edit Modal - TODO: Créer un composant modal dédié */}
        {isAccessModalOpen && selectedEnrollment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4">Modifier l'accès</h2>
              <p className="text-gray-600 mb-4">Modal à implémenter avec formulaire complet</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {
                  setIsAccessModalOpen(false)
                  setSelectedEnrollment(null)
                }}>
                  Annuler
                </Button>
                <Button onClick={() => handleUpdateAccess({})}>
                  Enregistrer
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Activate Course Modal */}
        {isActivateModalOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setIsActivateModalOpen(false)}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Activer le cours pour des utilisateurs</h2>
                  {selectedCourse && (
                    <p className="text-gray-600 mt-1">Cours: {selectedCourse.title}</p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setIsActivateModalOpen(false)
                    setSelectedUserIds([])
                    setUserSearchTerm("")
                    setAccessDurationDays("")
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleActivateCourse} className="space-y-6">
                {/* Sélection du cours si pas déjà sélectionné */}
                {!selectedCourseId && (
                  <div>
                    <Label htmlFor="activate-course-select" className="text-sm font-medium text-gray-700 mb-2 block">
                      Cours <span className="text-red-500">*</span>
                    </Label>
                    <select
                      id="activate-course-select"
                      value={selectedCourseId || ""}
                      onChange={(e) => setSelectedCourseId(e.target.value || null)}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                    >
                      <option value="">-- Sélectionner un cours --</option>
                      {coursesList.map((course) => (
                        <option key={course.id} value={course.id}>
                          {course.title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Recherche d'utilisateurs */}
                <div>
                  <Label htmlFor="user-search" className="text-sm font-medium text-gray-700 mb-2 block">
                    Rechercher des utilisateurs
                  </Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <Input
                      id="user-search"
                      type="text"
                      placeholder="Rechercher par nom ou email..."
                      value={userSearchTerm}
                      onChange={(e) => {
                        setUserSearchTerm(e.target.value)
                        refetchUsers()
                      }}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Liste des utilisateurs avec sélection multiple */}
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Sélectionner les utilisateurs <span className="text-red-500">*</span>
                  </Label>
                  <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                    {usersList.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <User className="mx-auto mb-2 text-gray-400" size={32} />
                        <p>Aucun utilisateur trouvé</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {usersList.map((user) => {
                          const isSelected = selectedUserIds.includes(user.id)
                          return (
                            <label
                              key={user.id}
                              className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer ${
                                isSelected ? "bg-blue-50" : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedUserIds([...selectedUserIds, user.id])
                                  } else {
                                    setSelectedUserIds(selectedUserIds.filter(id => id !== user.id))
                                  }
                                }}
                                className="mr-3 h-4 w-4 text-[#141835] focus:ring-[#141835] border-gray-300 rounded"
                              />
                              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#141835] flex items-center justify-center text-white font-medium mr-3">
                                {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || "U"}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {user.name || "Sans nom"}
                                </div>
                                <div className="text-sm text-gray-500 truncate">
                                  {user.email}
                                </div>
                              </div>
                              {isSelected && (
                                <CheckCircle className="ml-2 text-[#141835]" size={20} />
                              )}
                            </label>
                          )
                        })}
                      </div>
                    )}
                  </div>
                  {selectedUserIds.length > 0 && (
                    <div className="mt-2 text-sm text-gray-600">
                      {selectedUserIds.length} utilisateur(s) sélectionné(s)
                    </div>
                  )}
                </div>

                {/* Durée d'accès (optionnel) */}
                <div>
                  <Label htmlFor="access-duration" className="text-sm font-medium text-gray-700 mb-2 block flex items-center gap-2">
                    <Clock size={16} />
                    Durée d'accès (optionnel)
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="access-duration"
                      type="number"
                      min="1"
                      placeholder="90"
                      value={accessDurationDays}
                      onChange={(e) => setAccessDurationDays(e.target.value)}
                      className="w-32"
                    />
                    <span className="text-sm text-gray-600">jours</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Laisser vide pour un accès illimité. Si spécifié, l'accès expirera après ce nombre de jours.
                  </p>
                </div>

                {/* Utilisateurs sélectionnés (aperçu) */}
                {selectedUserIds.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Utilisateurs sélectionnés ({selectedUserIds.length})
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {selectedUserIds.map((userId) => {
                        const user = usersList.find(u => u.id === userId)
                        if (!user) return null
                        return (
                          <div
                            key={userId}
                            className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border border-gray-300 text-sm"
                          >
                            <span>{user.name || user.email}</span>
                            <button
                              type="button"
                              onClick={() => setSelectedUserIds(selectedUserIds.filter(id => id !== userId))}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsActivateModalOpen(false)
                      setSelectedUserIds([])
                      setUserSearchTerm("")
                      setAccessDurationDays("")
                    }}
                    disabled={isSubmittingActivation}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmittingActivation || !selectedCourseId || selectedUserIds.length === 0}
                    className="bg-[#141835] hover:bg-[#203a8a]"
                  >
                    {isSubmittingActivation ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Activation...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} className="mr-2" />
                        Activer pour {selectedUserIds.length} utilisateur(s)
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

